import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useProjects } from "../lib/projects";
import { useCustomers, useEventTypes } from "../lib/customers";
import { EmptyState, TableSkeleton } from "../components/states";
import { AddEventDialog } from "../components/AddEventDialog";
import { Combobox } from "../components/Combobox";

const PAGE_SIZE = 50;

export function CustomersPage() {
  const projectsQuery = useProjects();
  const projects = projectsQuery.data?.items ?? [];

  const [params, setParams] = useSearchParams();
  const projectParam = params.get("project");
  const tab = params.get("tab") ?? "AllEvents";
  const q = params.get("q") ?? "";
  const offset = parseInt(params.get("offset") ?? "0", 10);
  const includeBots = params.get("includeBots") === "true"; // default: bots hidden
  const [addEventOpen, setAddEventOpen] = useState(false);

  // Pick a sensible default project once projects load — first active one.
  useEffect(() => {
    if (projectParam || projects.length === 0) return;
    const firstActive = projects.find((p) => p.active) ?? projects[0];
    if (firstActive) {
      const next = new URLSearchParams(params);
      next.set("project", String(firstActive.id));
      setParams(next, { replace: true });
    }
  }, [projects, projectParam, params, setParams]);

  const projectId = projectParam ? parseInt(projectParam, 10) : null;
  const eventType = tab === "AllEvents" ? undefined : tab;

  // Event-Types kommen aus der DB: SELECT DISTINCT event_type — zeigt
  // immer was das Projekt tatsächlich loggt, inkl. custom-Typen wie
  // softLead / SubmitApplication.
  const eventTypesQuery = useEventTypes(projectId);
  const projectEventTypes = eventTypesQuery.data?.items ?? [];

  const customersQuery = useCustomers({
    projectId,
    eventType,
    q: q || undefined,
    limit: PAGE_SIZE,
    offset,
    includeBots,
  });

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    // Any filter change resets the page.
    if (key !== "offset") next.delete("offset");
    setParams(next, { replace: true });
  };

  const items = customersQuery.data?.items ?? [];
  const total = customersQuery.data?.total ?? 0;
  const bqHasOlder = customersQuery.data?.bqHasOlder ?? false;
  const eventTypesSource = eventTypesQuery.data?.source ?? "pg";
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-content space-y-6">
        <header className="flex items-baseline justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
              Customers
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
              Visitors & Journeys
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {projectId && (
              <button
                type="button"
                onClick={() => setAddEventOpen(true)}
                className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover"
              >
                <Plus size={14} /> Event hinzufügen
              </button>
            )}
            <ProjectPicker
              projects={projects}
              value={projectId}
              onChange={(id) => setParam("project", String(id))}
            />
          </div>
        </header>

        {projectId && (
          <AddEventDialog
            open={addEventOpen}
            onOpenChange={setAddEventOpen}
            projectId={projectId}
            defaultEventType={eventType}
          />
        )}

        <div className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2">
          <Search size={14} className="text-text-tertiary" />
          <input
            type="search"
            value={q}
            onChange={(e) => setParam("q", e.target.value)}
            placeholder="Email, Telefon, Client-ID, Order-ID oder Visitor-ID"
            className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
          />
          {q && (
            <button
              type="button"
              onClick={() => setParam("q", null)}
              className="text-xs text-text-tertiary hover:text-text-primary"
            >
              ×
            </button>
          )}
          <label className="flex shrink-0 items-center gap-1.5 border-l border-border-primary pl-3 text-xs text-text-tertiary select-none">
            <input
              type="checkbox"
              checked={includeBots}
              onChange={(e) => setParam("includeBots", e.target.checked ? "true" : null)}
              className="accent-accent"
            />
            Bots anzeigen
          </label>
        </div>

        <TabBar
          active={tab}
          presets={["AllEvents", ...projectEventTypes.map((e) => e.eventType)]}
          counts={Object.fromEntries(
            projectEventTypes.map((e) => [e.eventType, e.count]),
          )}
          onChange={(next) => setParam("tab", next)}
        />
        <p className="text-[11px] text-text-tertiary">
          {eventTypesSource === "bq"
            ? "Counts: all-time aus BigQuery."
            : "Counts: letzte 35 Tage aus Postgres (BigQuery-Sync für dieses Projekt deaktiviert)."}
          {q && bqHasOlder && (
            <>
              {" · "}Suche schließt dormant Visitor aus BigQuery ein
              (markiert mit <code className="rounded bg-bg-tertiary px-1 font-mono text-[10px]">BQ</code>).
            </>
          )}
        </p>

        {!projectId ? (
          <EmptyState title="Kein Projekt ausgewählt" />
        ) : customersQuery.isLoading ? (
          <TableSkeleton rows={6} />
        ) : customersQuery.error ? (
          <div className="rounded-md border border-danger bg-danger-bg px-3 py-2 text-sm text-danger">
            {String((customersQuery.error as Error).message)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Keine Customers gefunden"
            body={
              q
                ? `Suche „${q}" hat nichts ergeben.`
                : eventType
                  ? `Noch kein Visitor mit Event-Typ ${eventType}.`
                  : "Dieses Projekt hat noch keine Visitors."
            }
          />
        ) : (
          <>
            <CustomerTable items={items} projectId={projectId} />
            <Pagination
              offset={offset}
              total={total}
              pageSize={PAGE_SIZE}
              hasPrev={hasPrev}
              hasNext={hasNext}
              onPrev={() =>
                setParam("offset", String(Math.max(0, offset - PAGE_SIZE)))
              }
              onNext={() => setParam("offset", String(offset + PAGE_SIZE))}
            />
          </>
        )}
      </div>
    </div>
  );
}

function ProjectPicker({
  projects,
  value,
  onChange,
}: {
  projects: Array<{ id: number; name: string; active: boolean }>;
  value: number | null;
  onChange: (id: number) => void;
}) {
  return (
    <div className="w-64">
      <Combobox
        value={value != null ? String(value) : ""}
        options={projects.map((p) => ({
          value: String(p.id),
          label: `${p.name}${!p.active ? " (paused)" : ""}`,
        }))}
        onChange={(v) => {
          if (v) onChange(parseInt(v, 10));
        }}
        placeholder={projects.length === 0 ? "Keine Projekte" : "Projekt suchen…"}
        clearable={false}
      />
    </div>
  );
}

function TabBar({
  active,
  presets,
  counts,
  onChange,
}: {
  active: string;
  presets: string[];
  counts: Record<string, number>;
  onChange: (next: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-border-primary">
      {presets.map((p) => {
        const isActive = active === p;
        const count = p === "AllEvents" ? undefined : counts[p];
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`relative -mb-px flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
              isActive
                ? "border-b-2 border-accent text-accent"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            {p === "AllEvents" ? "All Events" : p}
            {count !== undefined && (
              <span
                className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] ${
                  isActive
                    ? "bg-accent/15 text-accent"
                    : "bg-bg-tertiary text-text-tertiary"
                }`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function CustomerTable({
  items,
  projectId,
}: {
  items: Array<import("../lib/customers").CustomerListItem>;
  projectId: number;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-border-primary">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-bg-secondary">
          <tr className="text-left text-xs uppercase tracking-wider text-text-tertiary">
            <th className="w-1/3 px-3 py-2 font-medium">Customer</th>
            <th className="w-1/4 px-3 py-2 font-medium">Last Action</th>
            <th className="w-1/5 px-3 py-2 font-medium">Source / Medium</th>
            <th className="w-1/5 px-3 py-2 font-medium">Campaign</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr
              key={c.visitorId}
              className="border-t border-border-secondary hover:bg-bg-hover"
            >
              <td className="truncate px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/customers/${projectId}/${c.visitorId}`}
                    className="font-medium text-text-link hover:text-text-link-hover"
                  >
                    {c.displayName}
                  </Link>
                  {c.source === "bq" && (
                    <span
                      className="rounded px-1 py-0.5 font-mono text-[9px] font-semibold uppercase text-accent bg-accent/15"
                      title="Dormant Visitor — Last-Action aus BigQuery, älter als 35 Tage"
                    >
                      BQ
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] text-text-tertiary">
                  id {c.visitorId}
                  {c.source === "pg" && (
                    <>
                      {" "}· {c.eventCount} event
                      {c.eventCount === 1 ? "" : "s"} (35d)
                    </>
                  )}
                </div>
              </td>
              <td className="truncate px-3 py-2.5">
                <div className="text-text-primary">{c.lastEventType}</div>
                <div className="font-mono text-[10px] text-text-tertiary">
                  {new Date(c.lastActionAt).toLocaleString("de-DE")}
                </div>
              </td>
              <td className="truncate px-3 py-2.5 text-xs text-text-secondary">
                {c.utmSource ?? "(direct)"} / {c.utmMedium ?? "(none)"}
              </td>
              <td className="truncate px-3 py-2.5 text-xs text-text-secondary">
                {c.utmCampaign ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({
  offset,
  total,
  pageSize,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
}: {
  offset: number;
  total: number;
  pageSize: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + pageSize, total);
  return (
    <div className="flex items-center justify-between text-xs text-text-tertiary">
      <span>
        {from}–{to} von {total}
      </span>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasPrev}
          className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover disabled:opacity-40"
        >
          <ChevronLeft size={12} /> Vor
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasNext}
          className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover disabled:opacity-40"
        >
          Weiter <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}

